import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import './AdminPanel.css';
import apiClient, { permissionMessage } from '../config/axiosConfig';
import BikeEditForm from './BikeEditForm';
import AddLocation from './AddLocation';
import AddBike from './AddBike';
import LocationSelector from './LocationSelector';
import LocationManagers from './LocationManagers';
import LocationEmailRecipients from './LocationEmailRecipients';
import LocationRecurrenceSetting from './LocationRecurrenceSetting';
import LocationRentalNotice from './LocationRentalNotice';
import MyEmailPreference from './MyEmailPreference';
import SuperAdminUsers from './SuperAdminUsers';
import NoAccess from './NoAccess';
import ChooseLocation from './ChooseLocation';
import AdminWeekAgenda, { referenceLabel, statusLabel } from './AdminWeekAgenda';
import { formatAmsterdam } from '../utils/time';

interface Reservation {
  id: number;
  // Het korte reserveringsnummer (DF-XXXX-XXXX); leeg bij oude reserveringen.
  reference_code?: string | null;
  bike_id: number;
  start_at: string;
  end_at: string;
  reserver_name: string;
  reserver_email: string;
  reserver_phone: string;
  status: string;
  created_at: string;
  notes?: string;
  cancellation_reason?: string;
  // Gevuld als deze keer bij een wekelijks herhalende reservering hoort.
  series_id?: number | null;
  bike?: {
    id: number;
    asset_code: string;
    bike_type: {
      id: number;
      name: string;
    };
    location: {
      id: number;
      name: string;
      city: string;
    };
  };
}

interface Bike {
  id: number;
  asset_code: string;
  bike_type_id: number;
  location_id: number;
  status: string;
  notes?: string;
  send_manager_email?: boolean;
  bike_type_name: string;
  location_name: string;
}

interface Location {
  id: number;
  name: string;
  city: string;
  address_line1?: string;
  postal_code?: string;
  // Hoeveel weken vooruit een herhalende reservering hier mag lopen.
  max_recurrence_weeks?: number;
  // De melding over huurkosten en het protocol op het reserveringsformulier.
  rental_notice_enabled?: boolean;
  rental_notice_text?: string | null;
  rental_notice_url?: string | null;
}

/** Raakt een annulering alleen deze keer, of alle nog komende keren? */
type Scope = 'single' | 'series';

interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
}

// Formatteert een datum/tijd naar het Nederlandse formaat in Amsterdamse tijd:
// dd-mm-yyyy hh:mm
const formatDateTime = (isoString: string): string => {
  const formatted = formatAmsterdam(isoString, 'DD-MM-YYYY HH:mm');
  return formatted === 'Invalid date' ? isoString : formatted;
};

const AdminPanel: React.FC = () => {
  const { user, logout, isLoading: authLoading, isAuthenticated, refreshProfile } = useAuth();
  const { isSuperAdmin, managedLocations, managedLocationIds, hasAccess, needsLocationChoice } =
    usePermissions();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [bikes, setBikes] = useState<Bike[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'reservations' | 'bikes' | 'locations' | 'settings'>('reservations');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBikeStatus, setSelectedBikeStatus] = useState<string | 'all'>('all');
  const [editingBike, setEditingBike] = useState<Bike | null>(null);
  const [showBikeModal, setShowBikeModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showAddBikeModal, setShowAddBikeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingReservation, setCancellingReservation] = useState<Reservation | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  // Bij een herhalende reservering kiest de beheerder of het alleen deze keer
  // raakt of alle nog komende keren. Standaard alleen deze keer.
  const [cancelScope, setCancelScope] = useState<Scope>('single');
  const [isCancelling, setIsCancelling] = useState(false);
  // De agenda toont naam en reserveringsnummer; e-mail, telefoon en notities
  // komen pas in beeld als de beheerder een blok aanklikt.
  const [detailReservation, setDetailReservation] = useState<Reservation | null>(null);

  // Alleen de locaties waar deze beheerder iets mag; een superadmin ziet alles.
  const visibleLocations = useMemo(
    () =>
      isSuperAdmin
        ? locations
        : locations.filter((location) => managedLocationIds.includes(location.id)),
    [locations, isSuperAdmin, managedLocationIds]
  );

  // De eigen koppeling met de geopende locatie, nodig voor het e-mailschuifje.
  const myLocationLink = useMemo(
    () => managedLocations.find((location) => location.id === selectedLocation?.id) || null,
    [managedLocations, selectedLocation]
  );

  /** Een 403 betekent "geen rechten", geen verlopen sessie: dan niet uitloggen. */
  const handleApiError = (error: any, fallback: string) => {
    console.error(fallback, error);
    const message = permissionMessage(error);
    if (message) {
      setPermissionError(message);
      return;
    }
    if (error.response?.status === 401) {
      logout();
    }
  };

  useEffect(() => {
    fetchLocations(); // Always fetch locations
  }, []);

  // Wie maar één locatie beheert, hoeft niet eerst te kiezen.
  useEffect(() => {
    if (!selectedLocation && !isSuperAdmin && visibleLocations.length === 1) {
      setSelectedLocation(visibleLocations[0]);
    }
  }, [selectedLocation, isSuperAdmin, visibleLocations]);

  useEffect(() => {
    if (selectedLocation) {
      if (activeTab === 'reservations') {
        fetchReservations();
      } else if (activeTab === 'bikes') {
        fetchBikes();
      }
    }
  }, [activeTab, selectedLocation]);

  const fetchReservations = async () => {
    if (!selectedLocation) return;
    
    try {
      setIsLoading(true);
      // De backend geeft alleen reserveringen van de eigen locaties terug; hier
      // beperken we het verder tot de locatie die nu open staat.
      const response = await apiClient.get<ApiResponse<Reservation[]>>('/reservations');
      const filteredReservations = (response.data.data || []).filter(reservation => 
        reservation.bike?.location?.id === selectedLocation.id
      );
      setReservations(filteredReservations);
    } catch (error: any) {
      handleApiError(error, 'Error fetching reservations:');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBikes = async () => {
    if (!selectedLocation) return;
    
    try {
      setIsLoading(true);
      const response = await apiClient.get<ApiResponse<Bike[]>>('/bikes');
      // Filter bikes for the selected location
      const filteredBikes = (response.data.data || []).filter(bike => 
        bike.location_id === selectedLocation.id
      );
      setBikes(filteredBikes);
    } catch (error: any) {
      handleApiError(error, 'Error fetching bikes:');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async (): Promise<void> => {
    try {
      const response = await apiClient.get<ApiResponse<Location[]>>('/locations');
      setLocations(response.data.data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  // Filter bikes by status only (location filtering is done in fetchBikes)
  const filteredBikes = bikes.filter(bike => {
    const statusMatch = selectedBikeStatus === 'all' || bike.status === selectedBikeStatus;
    return statusMatch;
  });

  /** Geeft terug of er daadwerkelijk verwijderd is, zodat het detailvenster
   *  open kan blijven als de beheerder de bevestiging afbreekt. */
  const deleteReservation = async (id: number): Promise<boolean> => {
    if (!window.confirm('Weet je zeker dat je deze reservering wilt verwijderen?')) {
      return false;
    }

    try {
      await apiClient.delete(`/reservations/${id}`);
      fetchReservations();
      return true;
    } catch (error: any) {
      handleApiError(error, 'Error deleting reservation:');
      return false;
    }
  };

  const openCancelModal = (reservation: Reservation) => {
    setDetailReservation(null);
    setCancellingReservation(reservation);
    setCancellationReason('');
    setCancelScope('single');
    setShowCancelModal(true);
  };

  const cancelReservation = async () => {
    if (!cancellingReservation || !cancellationReason.trim()) {
      alert('Reden voor annulering is verplicht');
      return;
    }

    setIsCancelling(true);
    try {
      const cancelsWholeSeries = cancelScope === 'series' && Boolean(cancellingReservation.series_id);

      await apiClient.patch(`/reservations/${cancellingReservation.id}/cancel`, {
        cancellation_reason: cancellationReason.trim(),
        scope: cancelsWholeSeries ? 'series' : 'single'
      });
      setShowCancelModal(false);
      setCancellingReservation(null);
      setCancellationReason('');
      fetchReservations();
    } catch (error: any) {
      const message = permissionMessage(error);
      if (!message && error.response?.status !== 401) {
        alert('Er is een fout opgetreden bij het annuleren van de reservering');
      }
      handleApiError(error, 'Error cancelling reservation:');
    } finally {
      setIsCancelling(false);
    }
  };

  const updateBike = async (bikeData: Partial<Bike>) => {
    if (!editingBike) return;
    
    try {
      await apiClient.put(`/bikes/${editingBike.id}`, bikeData);
      setShowBikeModal(false);
      setEditingBike(null);
      fetchBikes();
    } catch (error: any) {
      handleApiError(error, 'Error updating bike:');
    }
  };

  const deleteBike = async (id: number) => {
    if (window.confirm('Weet je zeker dat je deze fiets wilt verwijderen?')) {
      try {
        await apiClient.delete(`/bikes/${id}`);
        fetchBikes();
      } catch (error: any) {
        handleApiError(error, 'Error deleting bike:');
      }
    }
  };

  const openBikeEditModal = (bike: Bike) => {
    setEditingBike(bike);
    setShowBikeModal(true);
  };

  const handleLocationAdded = () => {
    setShowAddLocationModal(false);
    fetchLocations();
    // Optionally auto-select the newly added location
    // This would require getting the new location data from the API
  };

  const handleBikeAdded = () => {
    setShowAddBikeModal(false);
    fetchBikes();
  };

  const handleLocationSelected = (location: Location) => {
    setSelectedLocation(location);
  };

  const handleBackToLocations = () => {
    setSelectedLocation(null);
    setReservations([]);
    setBikes([]);
  };

  // Show loading state while authentication is being checked
  if (authLoading) {
    return (
      <div className="admin-panel">
        <div className="admin-container">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            fontSize: '1.2rem'
          }}>
            Laden...
          </div>
        </div>
      </div>
    );
  }

  // Wie geblokkeerd is krijgt uitleg in plaats van een kale "toegang geweigerd".
  if (!isAuthenticated || !hasAccess) {
    return <NoAccess name={user?.name} email={user?.email} onLogout={logout} />;
  }

  // Eerste keer inloggen: eerst bepalen bij welke locatie iemand hoort.
  if (needsLocationChoice) {
    return <ChooseLocation />;
  }

  // Beheerdersbeheer is een apart scherm en alleen voor de superadmin
  if (showUserManagement && isSuperAdmin) {
    return <SuperAdminUsers locations={locations} onClose={() => setShowUserManagement(false)} />;
  }

  // Show location selector if no location is selected
  if (!selectedLocation) {
    return (
      <>
        <LocationSelector 
          onLocationSelected={handleLocationSelected} 
          onAddLocation={() => setShowAddLocationModal(true)}
          onManageUsers={() => setShowUserManagement(true)}
          locations={visibleLocations}
          isLoading={isLoading}
          onRefreshLocations={fetchLocations}
          canManageLocations={isSuperAdmin}
        />
        
        {/* Add Location Modal */}
        {showAddLocationModal && isSuperAdmin && (
          <div className="modal-overlay">
            <AddLocation
              onLocationAdded={handleLocationAdded}
              onCancel={() => setShowAddLocationModal(false)}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-container">
        <div className="admin-header">
          <div className="header-left">
            {(isSuperAdmin || visibleLocations.length > 1) && (
              <button 
                className="back-to-locations-btn"
                onClick={handleBackToLocations}
              >
                ← Terug naar locaties
              </button>
            )}
            <div className="location-info">
              <h1>Beheer Paneel</h1>
              <p className="selected-location">{selectedLocation.name} - {selectedLocation.city}</p>
            </div>
          </div>
          <div className="user-info">
            <span>Welkom, {user?.name}</span>
            {isSuperAdmin && (
              <button onClick={() => setShowUserManagement(true)} className="header-action-btn">
                Beheerders
              </button>
            )}
            <button onClick={logout} className="logout-btn">Uitloggen</button>
          </div>
        </div>

        {permissionError && (
          <div className="permission-banner">
            <span>{permissionError}</span>
            <button
              type="button"
              className="permission-banner-close"
              onClick={() => setPermissionError(null)}
              aria-label="Melding sluiten"
            >
              ×
            </button>
          </div>
        )}

        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'reservations' ? 'active' : ''}`}
            onClick={() => setActiveTab('reservations')}
          >
            Reserveringen
          </button>
          <button
            className={`tab-button ${activeTab === 'bikes' ? 'active' : ''}`}
            onClick={() => setActiveTab('bikes')}
          >
            Fietsen
          </button>
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Instellingen
          </button>
        </div>

        {!isSuperAdmin && myLocationLink && (
          <MyEmailPreference
            locationId={selectedLocation.id}
            locationName={selectedLocation.name}
            receiveEmails={myLocationLink.receive_emails}
            onChanged={refreshProfile}
          />
        )}

        <div className="tab-content">
          {activeTab === 'reservations' && (
            <div className="reservations-tab">
              <div className="reservations-header">
                <h2>Reserveringen Beheren</h2>
              </div>

              <AdminWeekAgenda
                reservations={reservations}
                isLoading={isLoading}
                onSelectReservation={setDetailReservation}
              />
            </div>
          )}

          {activeTab === 'bikes' && (
            <div className="bikes-tab">
              <div className="bikes-header">
                <h2>Fietsen Beheren</h2>
                <button 
                  className="btn btn-primary add-btn"
                  onClick={() => setShowAddBikeModal(true)}
                >
                  + Fiets Toevoegen
                </button>
              </div>
              <div className="bikes-filters">
                <div className="filter-group">
                  <label htmlFor="bike-status-filter">Filter op status:</label>
                  <select
                    id="bike-status-filter"
                    value={selectedBikeStatus}
                    onChange={(e) => setSelectedBikeStatus(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">Alle statussen</option>
                    <option value="available">Beschikbaar</option>
                    <option value="maintenance">Onderhoud</option>
                    <option value="retired">Buiten gebruik</option>
                  </select>
                </div>
              </div>
              {isLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Fietsen laden...</p>
                </div>
              ) : (
                <div className="bikes-grid">
                  {filteredBikes.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">📋</div>
                      <p>Geen fietsen gevonden{selectedBikeStatus !== 'all' ? ' met de geselecteerde status filter' : ' voor deze locatie'}.</p>
                    </div>
                  ) : (
                    filteredBikes.map((bike) => (
                      <div key={bike.id} className="bike-card">
                        <div className="bike-card-header">
                          <div className="bike-asset-code">{bike.asset_code}</div>
                          <div className={`bike-status ${bike.status}`}>
                            {bike.status === 'available' ? 'Beschikbaar' : 
                             bike.status === 'maintenance' ? 'Onderhoud' : 
                             bike.status === 'retired' ? 'Buiten gebruik' : bike.status}
                          </div>
                        </div>
                        <div className="bike-card-body">
                          <div className="bike-info">
                            <div className="info-item">
                              <span className="info-label">Type:</span>
                              <span className="info-value">{bike.bike_type_name}</span>
                            </div>
                            <div className="info-item">
                              <span className="info-label">Locatie:</span>
                              <span className="info-value">{bike.location_name}</span>
                            </div>
                            {bike.notes && (
                              <div className="info-item notes">
                                <span className="info-label">Notities:</span>
                                <span className="info-value">{bike.notes}</span>
                              </div>
                            )}
                            <div className="info-item">
                              <span className="info-label">E-mail naar beheerders:</span>
                              <span className="info-value">{bike.send_manager_email ? 'Aan' : 'Uit'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="bike-card-actions">
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => openBikeEditModal(bike)}
                          >
                            Bewerken
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => deleteBike(bike.id)}
                          >
                            Verwijderen
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="settings-tab">
              {isSuperAdmin && (
                <LocationManagers location={selectedLocation} onProfileChanged={refreshProfile} />
              )}
              <LocationEmailRecipients location={selectedLocation} />
              <LocationRentalNotice
                location={selectedLocation}
                onSaved={(notice) => {
                  setLocations((current) =>
                    current.map((location) =>
                      location.id === selectedLocation.id ? { ...location, ...notice } : location
                    )
                  );
                  setSelectedLocation((current) => (current ? { ...current, ...notice } : current));
                }}
              />
              <LocationRecurrenceSetting
                location={selectedLocation}
                onSaved={(maxRecurrenceWeeks) => {
                  setLocations((current) =>
                    current.map((location) =>
                      location.id === selectedLocation.id
                        ? { ...location, max_recurrence_weeks: maxRecurrenceWeeks }
                        : location
                    )
                  );
                  setSelectedLocation((current) =>
                    current ? { ...current, max_recurrence_weeks: maxRecurrenceWeeks } : current
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bike Edit Modal */}
      {showBikeModal && editingBike && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ textAlign: "left" }}>
            <div className="modal-header" style={{ textAlign: "left" }}>
              <div>
                <h3 style={{ marginBottom: 0 }}>{editingBike.asset_code}</h3>
                <p className="modal-subtitle" style={{ marginTop: 4 }}>
                  {editingBike.bike_type_name} • {editingBike.location_name}
                </p>
              </div>
              <button 
                className="modal-close"
                onClick={() => setShowBikeModal(false)}
              >
                ×
              </button>
            </div>
            <BikeEditForm
              bike={editingBike}
              onSave={async (updatedFields) => {
                await updateBike(updatedFields);
              }}
              onCancel={() => setShowBikeModal(false)}
              locations={visibleLocations}
            />
          </div>
        </div>
      )}

      {/* Add Location Modal */}
      {showAddLocationModal && isSuperAdmin && (
        <div className="modal-overlay">
          <div className="modal-content">
            <AddLocation
              onLocationAdded={handleLocationAdded}
              onCancel={() => setShowAddLocationModal(false)}
            />
          </div>
        </div>
      )}

      {/* Add Bike Modal */}
      {showAddBikeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <AddBike
              onBikeAdded={handleBikeAdded}
              onCancel={() => setShowAddBikeModal(false)}
              locations={visibleLocations}
              defaultLocationId={selectedLocation.id}
            />
          </div>
        </div>
      )}

      {/* Reservation Detail Modal */}
      {detailReservation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3 style={{ marginBottom: 0 }}>Reservering</h3>
                <p className="modal-subtitle" style={{ marginTop: 4 }}>
                  {referenceLabel(detailReservation)}
                </p>
              </div>
              <button
                className="modal-close"
                onClick={() => setDetailReservation(null)}
                aria-label="Venster sluiten"
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: 'left' }}>
              <p><strong>Reserveringsnummer:</strong> {referenceLabel(detailReservation)}</p>
              <p><strong>Naam:</strong> {detailReservation.reserver_name}</p>
              <p><strong>E-mail:</strong> {detailReservation.reserver_email}</p>
              <p><strong>Telefoon:</strong> {detailReservation.reserver_phone || '-'}</p>
              <p><strong>Start:</strong> {formatDateTime(detailReservation.start_at)}</p>
              <p><strong>Eind:</strong> {formatDateTime(detailReservation.end_at)}</p>
              <p><strong>Status:</strong> {statusLabel(detailReservation.status)}</p>
              {detailReservation.series_id && (
                <p>
                  <span className="series-badge">🔁 Onderdeel van een wekelijkse herhaling</span>
                </p>
              )}
              {detailReservation.bike && (
                <>
                  <p><strong>Fiets:</strong> {detailReservation.bike.asset_code}</p>
                  <p><strong>Type:</strong> {detailReservation.bike.bike_type.name}</p>
                  <p><strong>Locatie:</strong> {detailReservation.bike.location.name} - {detailReservation.bike.location.city}</p>
                </>
              )}
              {detailReservation.notes && (
                <p><strong>Opmerkingen:</strong> {detailReservation.notes}</p>
              )}
              {detailReservation.cancellation_reason && (
                <p><strong>Annuleringsreden:</strong> {detailReservation.cancellation_reason}</p>
              )}
              <p><strong>Aangemaakt op:</strong> {formatDateTime(detailReservation.created_at)}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDetailReservation(null)}>
                Sluiten
              </button>
              {detailReservation.status !== 'cancelled' && detailReservation.status !== 'completed' && (
                <button className="cancel-btn" onClick={() => openCancelModal(detailReservation)}>
                  Annuleren
                </button>
              )}
              <button
                className="delete-btn"
                onClick={async () => {
                  const deleted = await deleteReservation(detailReservation.id);
                  if (deleted) {
                    setDetailReservation(null);
                  }
                }}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Reservation Modal */}
      {showCancelModal && cancellingReservation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Reservering Annuleren</h3>
              <button 
                className="modal-close"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: 'left' }}>
              <p style={{ textAlign: 'left' }}><strong>Status:</strong> {cancellingReservation.status === 'pending' ? 'In afwachting' : 
                cancellingReservation.status === 'confirmed' ? 'Bevestigd' : 
                cancellingReservation.status === 'completed' ? 'Voltooid' : 
                cancellingReservation.status === 'cancelled' ? 'Geannuleerd' : 
                cancellingReservation.status === 'no_show' ? 'Niet verschenen' : cancellingReservation.status}</p>
              {cancellingReservation.bike && (
                <>
                  <p style={{ textAlign: 'left' }}><strong>Fiets:</strong> {cancellingReservation.bike.asset_code} ({cancellingReservation.bike.bike_type.name})</p>
                  <p style={{ textAlign: 'left' }}><strong>Locatie:</strong> {cancellingReservation.bike.location.name} - {cancellingReservation.bike.location.city}</p>
                </>
              )}
              <p style={{ textAlign: 'left' }}><strong>Start:</strong> {formatDateTime(cancellingReservation.start_at)}</p>
              <p style={{ textAlign: 'left' }}><strong>Eind:</strong> {formatDateTime(cancellingReservation.end_at)}</p>

              {cancellingReservation.series_id && (
                <fieldset className="scope-choice">
                  <legend>Wat wil je annuleren?</legend>
                  <label>
                    <input
                      type="radio"
                      name="cancel-scope"
                      checked={cancelScope === 'single'}
                      onChange={() => setCancelScope('single')}
                      disabled={isCancelling}
                    />
                    <span>Alleen deze keer</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="cancel-scope"
                      checked={cancelScope === 'series'}
                      onChange={() => setCancelScope('series')}
                      disabled={isCancelling}
                    />
                    <span>Alle nog komende keren van deze wekelijkse herhaling</span>
                  </label>
                  <small className="scope-help">
                    {cancelScope === 'series'
                      ? 'Keren die al geweest zijn blijven staan. De reserveerder krijgt één e-mail met een overzicht van alles wat is geannuleerd, met dezelfde reden.'
                      : 'Deze reservering hoort bij een wekelijkse herhaling. Je kunt ook alle komende keren in één keer annuleren.'}
                  </small>
                </fieldset>
              )}

              <div className="form-group">
                <label htmlFor="cancellation-reason">
                  Reden voor annulering <span className="required">*</span>
                </label>
                <textarea
                  id="cancellation-reason"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Voer de reden voor annulering in..."
                  rows={4}
                  required
                  disabled={isCancelling}
                />
              </div>

              {isCancelling && (
                <div className="loading-message">
                  <div className="loading-spinner"></div>
                  <p>Email wordt verstuurd...</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
              >
                Annuleren
              </button>
              <button 
                className="btn btn-danger"
                onClick={cancelReservation}
                disabled={!cancellationReason.trim() || isCancelling}
              >
                {isCancelling ? 'Bezig met annuleren...' : 'Reservering Annuleren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
